// src/components/dashboard/LayoutSettingsModal.jsx
import React from 'react';

const LayoutSettingsModal = ({ widgetLayout, setWidgetLayout, onClose }) => {
  // Convert widgetLayout to an array for easier sorting
  const widgetArray = Object.entries(widgetLayout).map(([key, value]) => ({
    key,
    ...value,
    label: getWidgetLabel(key)
  })).sort((a, b) => a.position - b.position);

  // Get human-readable label for widget
  function getWidgetLabel(key) {
    const labels = {
      metrics: 'Cartes de métriques',
      activityChart: 'Graphique d\'activité',
      userChart: 'Répartition des utilisateurs',
      revenueChart: 'Tendance des revenus',
      engagementChart: 'Engagement des utilisateurs',
      performanceChart: 'Métriques de performance',
      comparativeChart: 'Analyse comparative',
      activities: 'Activités récentes',
      companyChart: 'Statut des entreprises'
    };
    return labels[key] || key;
  }

  // Handle visibility toggle
  const handleVisibilityToggle = (key) => {
    setWidgetLayout({
      ...widgetLayout,
      [key]: {
        ...widgetLayout[key],
        visible: !widgetLayout[key].visible
      }
    });
  };

  // Move widget up in the layout
  const moveWidgetUp = (key) => {
    const currentPosition = widgetLayout[key].position;
    if (currentPosition === 0) return;

    const previousWidget = Object.keys(widgetLayout).find(
      k => widgetLayout[k].position === currentPosition - 1
    );

    setWidgetLayout({
      ...widgetLayout,
      [key]: {
        ...widgetLayout[key],
        position: currentPosition - 1
      },
      [previousWidget]: {
        ...widgetLayout[previousWidget],
        position: currentPosition
      }
    });
  };

  // Move widget down in the layout
  const moveWidgetDown = (key) => {
    const currentPosition = widgetLayout[key].position;
    const maxPosition = Object.keys(widgetLayout).length - 1;
    if (currentPosition === maxPosition) return;

    const nextWidget = Object.keys(widgetLayout).find(
      k => widgetLayout[k].position === currentPosition + 1
    );

    setWidgetLayout({
      ...widgetLayout,
      [key]: {
        ...widgetLayout[key],
        position: currentPosition + 1
      },
      [nextWidget]: {
        ...widgetLayout[nextWidget],
        position: currentPosition
      }
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Personnaliser le tableau de bord</h3>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-4">
              Sélectionnez les widgets à afficher et organisez-les selon vos préférences.
            </p>
            
            <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
              {widgetArray.map((widget) => (
                <div 
                  key={widget.key} 
                  className="flex items-center justify-between p-3 border border-gray-200 rounded-lg"
                >
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={widget.visible}
                      onChange={() => handleVisibilityToggle(widget.key)}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="ml-3 text-sm font-medium text-gray-700">
                      {widget.label}
                    </span>
                  </div>
                  
                  <div className="flex space-x-1">
                    <button
                      onClick={() => moveWidgetUp(widget.key)}
                      disabled={widget.position === 0}
                      className={`p-1 rounded ${widget.position === 0 ? 'text-gray-300' : 'text-gray-600 hover:bg-gray-100'}`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => moveWidgetDown(widget.key)}
                      disabled={widget.position === Object.keys(widgetLayout).length - 1}
                      className={`p-1 rounded ${widget.position === Object.keys(widgetLayout).length - 1 ? 'text-gray-300' : 'text-gray-600 hover:bg-gray-100'}`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LayoutSettingsModal;